import React, { forwardRef } from 'react';
import logo from './gigaflight-logo.png';
import './CdcrForm.css';

/**
 * The CDCR (Form EF-115) laid out as one A4 page.
 * editable=false renders plain text so the page doubles as the PDF source.
 */

/** Calendar date as YYYY-MM-DD, taken from the date itself so a time zone cannot move it back a day. */
export const toDateInputValue = (value) => {
    if (!value) return '';
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value).trim());
    return match ? match[1] : '';
};

/** Today's date in the viewer's local calendar, not UTC. */
export const localToday = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

export const formatCdcrDate = (value) => {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(toDateInputValue(value));
    return iso ? `${iso[2]}/${iso[3]}/${iso[1]}` : (value || '');
};

const DRAWING_COLUMNS = [
    { key: 'affectedDrawingNo', label: 'Affected Drawing No.', width: '22%' },
    { key: 'drawingTitle', label: 'Drawing Title', width: '44%' },
    { key: 'currentRev', label: 'Current Rev', width: '17%' },
    { key: 'proposedRev', label: 'Proposed Rev', width: '17%' },
];

const MIN_DRAWING_ROWS = 3;

const CdcrFormPage = forwardRef(({ value, editable = false, onChange, exporting = false }, ref) => {
    const drawings = value.drawings || [];
    const signOffs = value.signOffs || [];

    const setField = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });

    const setDrawing = (index, field, fieldValue) => {
        const next = drawings.map((row, i) => (i === index ? { ...row, [field]: fieldValue } : row));
        onChange({ ...value, drawings: next });
    };

    const addDrawing = () => onChange({ ...value, drawings: [...drawings, {}] });

    const removeDrawing = (index) => onChange({ ...value, drawings: drawings.filter((_, i) => i !== index) });

    const setSignOff = (index, field, fieldValue) => {
        const next = signOffs.map((row, i) => (i === index ? { ...row, [field]: fieldValue } : row));
        onChange({ ...value, signOffs: next });
    };

    // Keep the printed table at least MIN_DRAWING_ROWS tall, like the paper form.
    const drawingRowCount = Math.max(drawings.length, editable ? 0 : MIN_DRAWING_ROWS);

    const renderOverviewField = (label, field, type = 'text') => (
        <div className="cdcr-field">
            <label>{label}</label>
            {editable ? (
                <input
                    type={type}
                    className="cdcr-input cdcr-field-value"
                    value={type === 'date' ? toDateInputValue(value[field]) : (value[field] || '')}
                    onChange={(e) => setField(field, e.target.value)}
                />
            ) : (
                <div className="cdcr-field-value">
                    {type === 'date' ? formatCdcrDate(value[field]) : value[field]}
                </div>
            )}
        </div>
    );

    const renderEntry = (field, sizeClass) => (editable ? (
        <textarea
            className={`cdcr-textarea ${sizeClass}`}
            value={value[field] || ''}
            placeholder="Add entry..."
            onChange={(e) => setField(field, e.target.value)}
        />
    ) : (
        <div className={`cdcr-entry ${sizeClass}`}>{value[field]}</div>
    ));

    const renderSignOffCell = (row, index, field, type = 'text') => {
        if (editable) {
            return (
                <input
                    type={type}
                    className="cdcr-input"
                    value={type === 'date' ? toDateInputValue(row[field]) : (row[field] || '')}
                    onChange={(e) => setSignOff(index, field, e.target.value)}
                />
            );
        }
        return type === 'date' ? formatCdcrDate(row[field]) : row[field];
    };

    const renderAction = (row, index) => {
        if (editable) {
            return (
                <select
                    className="cdcr-select"
                    value={row.action || ''}
                    onChange={(e) => setSignOff(index, 'action', e.target.value || null)}
                >
                    <option value="">Select...</option>
                    <option value="Approve">Approve</option>
                    <option value="Reject">Reject</option>
                </select>
            );
        }
        if (!row.action) return null;
        return (
            <span className={row.action === 'Approve' ? 'cdcr-action-approve' : 'cdcr-action-reject'}>
                {row.action}
            </span>
        );
    };

    return (
        <div ref={ref} className={`cdcr-page${exporting ? ' cdcr-exporting' : ''}`}>
            <header className="cdcr-header">
                <img className="cdcr-logo" src={logo} alt="GIGAFLIGHT Connectivity Inc." />
                <h1 className="cdcr-title">
                    CUSTOMER DRAWING<br />CHANGE REQUEST
                </h1>
            </header>

            <h2 className="cdcr-section-title">Request Overview</h2>
            <div className="cdcr-overview">
                {renderOverviewField('CDCR Number:', 'cdcrNumber')}
                {renderOverviewField('Date Requested:', 'dateRequested', 'date')}
                {renderOverviewField('Customer Name:', 'customerName')}
                {renderOverviewField('Originator Name:', 'originatorName')}
            </div>

            <h2 className="cdcr-section-title">Drawing Configuration Impact</h2>
            <table className="cdcr-table">
                <colgroup>
                    {DRAWING_COLUMNS.map((col) => <col key={col.key} style={{ width: col.width }} />)}
                </colgroup>
                <thead>
                    <tr>{DRAWING_COLUMNS.map((col) => <th key={col.key}>{col.label}</th>)}</tr>
                </thead>
                <tbody>
                    {Array.from({ length: drawingRowCount }).map((_, index) => {
                        const row = drawings[index] || {};
                        return (
                            <tr key={index}>
                                {DRAWING_COLUMNS.map((col, colIndex) => (
                                    <td key={col.key} className={colIndex === DRAWING_COLUMNS.length - 1 ? 'cdcr-row-wrap' : undefined}>
                                        {editable ? (
                                            <input
                                                className="cdcr-input"
                                                value={row[col.key] || ''}
                                                onChange={(e) => setDrawing(index, col.key, e.target.value)}
                                            />
                                        ) : row[col.key]}
                                        {editable && colIndex === DRAWING_COLUMNS.length - 1 && (
                                            <button
                                                type="button"
                                                className="cdcr-remove-row"
                                                title="Remove row"
                                                onClick={() => removeDrawing(index)}
                                            >
                                                <i className="fa fa-times-circle" aria-hidden="true" />
                                            </button>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {editable && (
                <button type="button" className="cdcr-add-row" onClick={addDrawing}>
                    <i className="fa fa-plus" aria-hidden="true" /> Add drawing row
                </button>
            )}

            <h2 className="cdcr-section-title">Description of Requested Change &amp; Justification</h2>
            <div className="cdcr-bar">Detailed Change Description</div>
            {renderEntry('changeDescription', 'cdcr-entry-description')}
            <div className="cdcr-bar">Reason for Change/Justification</div>
            {renderEntry('reasonForChange', 'cdcr-entry-reason')}

            <h2 className="cdcr-section-title">Review &amp; Sign-Offs</h2>
            <table className="cdcr-table cdcr-signoff-table">
                <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '24%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '16%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th>Role/Authority</th>
                        <th>Name</th>
                        <th>Signature</th>
                        <th>Date</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {signOffs.map((row, index) => (
                        <tr key={row.role || index}>
                            <td className="cdcr-role">{row.role}</td>
                            <td>{renderSignOffCell(row, index, 'name')}</td>
                            <td>{renderSignOffCell(row, index, 'signature')}</td>
                            <td>{renderSignOffCell(row, index, 'date', 'date')}</td>
                            <td>{renderAction(row, index)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <footer className="cdcr-footer">
                <span>
                    Confidential/Proprietary <em>- Approved XX - Computer revision is the company-wide latest revision – Printed pages are UNCONTROLLED</em>
                </span>
                <span className="cdcr-footer-form">Form#EF-115 Rev NR 8/17/2026 JG CDCR</span>
            </footer>
        </div>
    );
});

export default CdcrFormPage;
